import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { useAppContext } from '../context/AppContext';
import { useFeeManagement } from '../hooks/useFeeManagement';
import { formatCurrency } from '../utils/feeUtils';
import {
  Card,
  Button,
  Alert,
  Spinner,
  Badge,
  Row,
  Col,
  Table,
  Form,
  Modal,
  Dropdown,
  Tabs,
  Tab,
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/NewFeeManagement.css';
import {
  FaArrowLeft,
  FaUser,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaDollarSign,
  FaEdit,
  FaTrash,
  FaEllipsisV,
  FaPlus,
  FaPercentage,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const NewFeeManagement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedAcademicYear } = useAppContext();

  // Student and fees data
  const [student, setStudent] = useState(null);
  const [feeSheet, setFeeSheet] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [summary, setSummary] = useState({
    totalDue: 0,
    totalPaid: 0,
    totalPending: 0,
  });
  const [joinDate, setJoinDate] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [feeAssigned, setFeeAssigned] = useState(false);

  // Fee preview states
  const [feePreview, setFeePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Fee management hook
  const {
    getStudentFeeSheet,
    assignStudentFee,
    undoAssignStudentFee,
    makeInstallmentDue,
    loading: feeLoading,
    error: feeError,
  } = useFeeManagement(id, selectedAcademicYear?.id);

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [makingDueId, setMakingDueId] = useState(null);
  const [undoingDueId, setUndoingDueId] = useState(null);
  const [assigningFee, setAssigningFee] = useState(false);
  const [undoingAssignment, setUndoingAssignment] = useState(false);

  // Edit installment states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    amount: '',
    due_date: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Add installment states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    title: '',
    amount: '',
    due_date: '',
  });
  const [addingInstallment, setAddingInstallment] = useState(false);

  // Delete installment states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [installmentToDelete, setInstallmentToDelete] = useState(null);
  const [deletingInstallment, setDeletingInstallment] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('installments');

  // Fee adjustment states
  const [adjustments, setAdjustments] = useState([]);
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState(null);
  const [adjustmentForm, setAdjustmentForm] = useState({
    title: '',
    type: 'discount',
    amount: '',
  });
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [deleteAdjustmentModalOpen, setDeleteAdjustmentModalOpen] =
    useState(false);
  const [adjustmentToDelete, setAdjustmentToDelete] = useState(null);
  const [deletingAdjustment, setDeletingAdjustment] = useState(false);

  useEffect(() => {
    if (id) {
      fetchStudentData();
    } else {
      setError('Invalid Student ID.');
      setLoading(false);
    }
  }, [id, selectedAcademicYear]);

  useEffect(() => {
    // Set today's date as default join date
    const today = new Date().toISOString().split('T')[0];
    setJoinDate(today);
  }, []);

  useEffect(() => {
    if (!student?.batches?.length) {
      setSelectedBatchId('');
      return;
    }

    const currentSelectionExists = student.batches.some(
      (batch) => String(batch.batch_id) === String(selectedBatchId),
    );

    if (!currentSelectionExists) {
      setSelectedBatchId(String(student.batches[0].batch_id));
    }
  }, [student, selectedBatchId]);

  // Preview fee when student data is loaded and join date is available
  useEffect(() => {
    if (student && joinDate && selectedBatchId && !feeAssigned) {
      previewFee();
    }
  }, [student, joinDate, selectedBatchId, feeAssigned, selectedAcademicYear]);

  const handleJoinDateChange = (e) => {
    setJoinDate(e.target.value);
    // Preview will be triggered by the useEffect above
  };

  const getFeeAssignmentId = (feeSheetData) => {
    if (!feeSheetData) {
      return null;
    }

    return (
      feeSheetData.student_fee_assignment_id ||
      feeSheetData.fee_assignment_id ||
      feeSheetData.assignment_id ||
      feeSheetData.installments?.[0]?.student_fee_assignment_id ||
      feeSheetData.installments?.[0]?.fee_assignment_id ||
      feeSheetData.installments?.[0]?.assignment_id ||
      null
    );
  };

  const formatPreviewDate = (dateValue) => {
    if (!dateValue) {
      return '-';
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return dateValue;
    }

    return parsedDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatAdjustmentDateTime = (dateValue) => {
    if (!dateValue) {
      return '-';
    }

    const normalizedValue = String(dateValue).replace(' ', 'T');
    const parsedDate = new Date(normalizedValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return dateValue;
    }

    return parsedDate.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fetchFeeAdjustments = async () => {
    if (!id || !selectedAcademicYear?.id) {
      setAdjustments([]);
      return;
    }

    const token = getAuthToken();
    if (!token) {
      return;
    }

    setAdjustmentsLoading(true);

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get_fee_adjustments`,
        {
          params: {
            student_id: id,
            academic_year_id: selectedAcademicYear.id,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data?.status === 'success') {
        setAdjustments(response.data.data || []);
      } else {
        setAdjustments([]);
      }
    } catch (fetchError) {
      console.warn('Fee adjustments not available:', fetchError.message);
      setAdjustments([]);
    } finally {
      setAdjustmentsLoading(false);
    }
  };

  const fetchStudentData = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      navigate('/login');
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setError('Please select an academic year.');
      setLoading(false);
      return;
    }

    try {
      // Fetch student details
      const studentResponse = await axios.post(
        `${appConstants.API_ROOT_URL}get-student-v2`,
        {
          id: id,
          academic_year_id: selectedAcademicYear.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (
        studentResponse.data.status === 'success' &&
        studentResponse.data.data
      ) {
        setStudent(studentResponse.data.data);
        await fetchFeeAdjustments();

        // Try to fetch fee sheet data
        try {
          const feeSheetData = await getStudentFeeSheet();
          if (
            feeSheetData.status === 'success' &&
            feeSheetData.data &&
            feeSheetData.data.installments &&
            feeSheetData.data.installments.length > 0
          ) {
            setFeeSheet(feeSheetData.data);
            setInstallments(feeSheetData.data.installments || []);
            setFeeAssigned(true);

            // Calculate summary from installments
            const calculatedSummary = feeSheetData.data.installments.reduce(
              (acc, installment) => {
                const dueAmount = parseFloat(installment.amount) || 0;
                const paidAmount = parseFloat(installment.paid) || 0;
                const balanceAmount = parseFloat(installment.balance) || 0;

                acc.totalDue += dueAmount;
                acc.totalPaid += paidAmount;
                acc.totalPending += balanceAmount;

                return acc;
              },
              {
                totalDue: 0,
                totalPaid: 0,
                totalPending: 0,
              },
            );

            setSummary(calculatedSummary);
          } else {
            // API succeeded but no installments found
            console.warn('No installments found in fee sheet');
            setFeeAssigned(false);
          }
        } catch (feeErr) {
          console.warn('Fee sheet not available:', feeErr.message);
          setFeeAssigned(false);
        }
      } else {
        setError('Student not found.');
      }
    } catch (err) {
      setError('Failed to fetch student data.');
    }

    setLoading(false);
  };

  const previewFee = async () => {
    if (!selectedBatchId || !joinDate || !selectedAcademicYear?.id) {
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    const token = getAuthToken();

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}preview_fee`,
        {
          params: {
            student_id: id,
            batch_id: selectedBatchId,
            academic_year_id: selectedAcademicYear.id,
            join_date: joinDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data.status === 'success') {
        setFeePreview(response.data.data);
      } else {
        setPreviewError(response.data.message || 'Failed to fetch fee preview');
      }
    } catch (err) {
      setPreviewError(
        err.response?.data?.message || 'Failed to fetch fee preview',
      );
    }

    setPreviewLoading(false);
  };

  const handleAssignFee = async () => {
    if (assigningFee) {
      return;
    }

    if (!selectedBatchId) {
      setAlert({
        show: true,
        message: 'Please select a batch first.',
        variant: 'warning',
      });
      return;
    }

    setAssigningFee(true);
    setAlert({
      show: true,
      message: 'Assigning fee structure... Please wait.',
      variant: 'info',
    });

    try {
      const response = await assignStudentFee(selectedBatchId, joinDate);

      if (response.status === 'success') {
        setAlert({
          show: true,
          message: 'Fee structure assigned successfully!',
          variant: 'success',
        });
        await fetchStudentData();
      } else {
        setAlert({
          show: true,
          message: response.message || 'Failed to assign fee structure.',
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message: err.message || 'An error occurred while assigning fee.',
        variant: 'danger',
      });
    } finally {
      setAssigningFee(false);
    }
  };

  const handleMakeDue = async (installmentId) => {
    // Show confirmation dialog
    const installment = installments.find(
      (inst) => inst.installment_id === installmentId,
    );
    const installmentTitle = installment
      ? `${installment.title} (Installment ${installment.installment_no})`
      : 'this installment';

    const confirmed = window.confirm(
      `Are you sure you want to mark ${installmentTitle} as due?\n\nThis action will make the installment due for payment immediately.`,
    );

    if (!confirmed) {
      return;
    }

    setMakingDueId(installmentId);
    setAlert({
      show: true,
      message: 'Marking installment as due... Please wait.',
      variant: 'info',
    });

    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await makeInstallmentDue(installmentId, today);

      if (response.status === 'success') {
        setInstallments((prevInstallments) =>
          prevInstallments.map((installment) =>
            installment.installment_id === installmentId
              ? {
                  ...installment,
                  status: 'due',
                  due_date:
                    installment.due_date ||
                    new Date().toISOString().split('T')[0],
                }
              : installment,
          ),
        );

        setAlert({
          show: true,
          message: 'Installment marked as due successfully!',
          variant: 'success',
        });
        await fetchStudentData();
      } else {
        setAlert({
          show: true,
          message: response.message || 'Failed to make installment due.',
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message: err.message || 'An error occurred.',
        variant: 'danger',
      });
    } finally {
      setMakingDueId(null);
    }
  };

  const handleUndoMakeDue = async (installmentId) => {
    const installment = installments.find(
      (inst) => inst.installment_id === installmentId,
    );
    const installmentTitle = installment
      ? `${installment.title} (Installment ${installment.installment_no})`
      : 'this installment';

    const confirmed = window.confirm(
      `Warning: Undoing this action will only work if no receipt has been entered for ${installmentTitle}.\n\nAre you sure you want to undo make due?`,
    );
    if (!confirmed) {
      return;
    }

    setUndoingDueId(installmentId);
    setAlert({
      show: true,
      message: 'Undoing installment due... Please wait.',
      variant: 'info',
    });
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Unauthorized. Please log in again.');
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}undo-make-installment-due`,
        {
          student_fee_installment_id: installmentId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data?.status === 'success') {
        setAlert({
          show: true,
          message: 'Installment due status was successfully undone.',
          variant: 'success',
        });
        fetchStudentData();
      } else {
        setAlert({
          show: true,
          message:
            response.data?.message ||
            'Failed to undo make due. This installment may already have a receipt entered.',
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message:
          err.response?.data?.message ||
          err.message ||
          'Error undoing make due.',
        variant: 'danger',
      });
    } finally {
      setUndoingDueId(null);
    }
  };

  const handleUndoAssignFee = async () => {
    if (undoingAssignment) {
      return;
    }

    const assignmentId = getFeeAssignmentId(feeSheet);
    if (!assignmentId) {
      setAlert({
        show: true,
        message:
          'Fee assignment id not found. Unable to undo this fee assignment.',
        variant: 'danger',
      });
      return;
    }

    const confirmed = window.confirm(
      'Warning: This will undo the assigned fee structure for this student in the selected academic year. Continue?',
    );

    if (!confirmed) {
      return;
    }

    setUndoingAssignment(true);
    setAlert({
      show: true,
      message: 'Undoing fee assignment... Please wait.',
      variant: 'info',
    });

    try {
      const response = await undoAssignStudentFee(assignmentId);

      if (response.status === 'success') {
        setAlert({
          show: true,
          message: response.message || 'Fee assignment undone successfully!',
          variant: 'success',
        });
        await fetchStudentData();
      } else {
        setAlert({
          show: true,
          message: response.message || 'Failed to undo fee assignment.',
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message:
          err.response?.data?.message ||
          err.message ||
          'An error occurred while undoing fee assignment.',
        variant: 'danger',
      });
    } finally {
      setUndoingAssignment(false);
    }
  };

  const getDueDateStatus = (dueDate, balance, paidAmount) => {
    const numBalance = parseFloat(balance) || 0;
    const numPaid = parseFloat(paidAmount) || 0;

    if (numBalance === 0 || (numPaid > 0 && numBalance === 0)) {
      return { type: 'paid', daysDiff: 0 };
    }

    const today = new Date();
    const due = new Date(dueDate);
    const timeDiff = due.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
      return { type: 'overdue', daysDiff: Math.abs(daysDiff) };
    } else if (daysDiff === 0) {
      return { type: 'due_today', daysDiff: 0 };
    } else if (daysDiff <= 7) {
      return { type: 'due_soon', daysDiff };
    } else {
      return { type: 'upcoming', daysDiff };
    }
  };

  const getStatusBadge = (status, balance, dueDate, paidAmount) => {
    if (!dueDate) {
      // Fallback to original logic if no due date
      switch (status) {
        case 'paid':
          return <Badge bg="success">Paid</Badge>;
        case 'due':
          return <Badge bg="warning">Due</Badge>;
        case 'pending':
        default:
          return <Badge bg="secondary">Pending</Badge>;
      }
    }

    const dueDateStatus = getDueDateStatus(dueDate, balance, paidAmount);

    if (dueDateStatus.type === 'paid') {
      return <Badge bg="success">Paid</Badge>;
    } else if (dueDateStatus.type === 'overdue') {
      return (
        <div className="d-flex flex-column align-items-center">
          <Badge bg="danger">Overdue</Badge>
          <small className="text-danger" style={{ fontSize: '0.65rem' }}>
            {dueDateStatus.daysDiff} days ago
          </small>
        </div>
      );
    } else if (dueDateStatus.type === 'due_today') {
      return <Badge bg="warning">Due Today</Badge>;
    } else if (dueDateStatus.type === 'due_soon') {
      return (
        <div className="d-flex flex-column align-items-center">
          <Badge bg="warning">Due Soon</Badge>
          <small className="text-warning" style={{ fontSize: '0.65rem' }}>
            in {dueDateStatus.daysDiff} days
          </small>
        </div>
      );
    } else {
      return (
        <div className="d-flex flex-column align-items-center">
          <Badge bg="secondary">Upcoming</Badge>
          <small className="text-muted" style={{ fontSize: '0.65rem' }}>
            in {dueDateStatus.daysDiff} days
          </small>
        </div>
      );
    }
  };

  const getRowClassName = (status, dueDate, balance, paidAmount) => {
    if (!dueDate) {
      // Fallback to original logic
      switch (status) {
        case 'paid':
          return 'table-success-light';
        case 'due':
          return 'table-warning-light';
        case 'pending':
        default:
          return '';
      }
    }

    const dueDateStatus = getDueDateStatus(dueDate, balance, paidAmount);

    switch (dueDateStatus.type) {
      case 'paid':
        return 'table-success-light';
      case 'overdue':
        return 'table-danger-light';
      case 'due_today':
      case 'due_soon':
        return 'table-warning-light';
      case 'upcoming':
      default:
        return '';
    }
  };

  const getInstallmentBadge = (installmentNo, status) => {
    const badgeClass =
      status === 'paid'
        ? 'bg-success'
        : status === 'due'
          ? 'bg-warning'
          : 'bg-secondary';
    return (
      <Badge className={`rounded-circle ${badgeClass} me-2 px-2 py-1`}>
        {installmentNo}
      </Badge>
    );
  };

  const handleEditInstallment = (installment) => {
    setEditingInstallment(installment);
    setEditForm({
      title: installment.title || '',
      amount: installment.amount || '',
      due_date: installment.due_date || '',
    });
    setEditModalOpen(true);
  };

  const handleOpenAddInstallment = () => {
    setAddForm({
      title: '',
      amount: '',
      due_date: new Date().toISOString().split('T')[0],
    });
    setAddModalOpen(true);
  };

  const handleSaveAddInstallment = async () => {
    const assignmentId = getFeeAssignmentId(feeSheet);

    if (!assignmentId) {
      setAlert({
        show: true,
        message: 'Fee assignment id not found. Unable to add installment.',
        variant: 'danger',
      });
      return;
    }

    if (!addForm.title.trim() || !addForm.amount || !addForm.due_date) {
      setAlert({
        show: true,
        message: 'Title, amount, and due date are required.',
        variant: 'warning',
      });
      return;
    }

    setAddingInstallment(true);
    const token = getAuthToken();

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}add_installment`,
        {
          student_fee_assignment_id: assignmentId,
          title: addForm.title.trim(),
          amount: parseFloat(addForm.amount),
          due_date: addForm.due_date,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data?.status === 'success') {
        setAddModalOpen(false);
        setAlert({
          show: true,
          message: 'Installment added successfully!',
          variant: 'success',
        });
        await fetchStudentData();
      } else {
        setAlert({
          show: true,
          message: response.data?.message || 'Failed to add installment.',
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message:
          err.response?.data?.message ||
          err.message ||
          'Error adding installment.',
        variant: 'danger',
      });
    } finally {
      setAddingInstallment(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingInstallment) {
      return;
    }
    setSavingEdit(true);
    const token = getAuthToken();
    try {
      const payload = {
        installment_id: editingInstallment.installment_id,
        title: editForm.title,
        amount: parseFloat(editForm.amount),
        due_date: editForm.due_date,
      };

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}edit_installment`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data?.status === 'success') {
        setEditModalOpen(false);
        setAlert({
          show: true,
          message: 'Installment updated successfully!',
          variant: 'success',
        });
        fetchStudentData();
      } else {
        setAlert({
          show: true,
          message: response.data?.message || 'Failed to update installment.',
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message:
          err.response?.data?.message ||
          err.message ||
          'Error updating installment.',
        variant: 'danger',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteInstallment = (installment) => {
    setInstallmentToDelete(installment);
    setDeleteConfirmOpen(true);
  };

  const handleOpenAddAdjustment = () => {
    setEditingAdjustment(null);
    setAdjustmentForm({
      title: '',
      type: 'discount',
      amount: '',
    });
    setAdjustmentModalOpen(true);
  };

  const handleOpenEditAdjustment = (adjustment) => {
    setEditingAdjustment(adjustment);
    setAdjustmentForm({
      title: adjustment.title || '',
      type: adjustment.type || 'discount',
      amount: adjustment.amount || '',
    });
    setAdjustmentModalOpen(true);
  };

  const handleSaveAdjustment = async () => {
    if (!selectedAcademicYear?.id) {
      setAlert({
        show: true,
        message: 'Academic year is required to save adjustment.',
        variant: 'danger',
      });
      return;
    }

    if (!adjustmentForm.title.trim() || !adjustmentForm.amount) {
      setAlert({
        show: true,
        message: 'Title, type, and amount are required.',
        variant: 'warning',
      });
      return;
    }

    const token = getAuthToken();
    setSavingAdjustment(true);

    try {
      const payload = {
        student_id: Number(id),
        academic_year_id: selectedAcademicYear.id,
        title: adjustmentForm.title.trim(),
        type: adjustmentForm.type,
        amount: parseFloat(adjustmentForm.amount),
      };

      if (editingAdjustment?.id) {
        payload.adjustment_id = editingAdjustment.id;
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${
          editingAdjustment ? 'edit_fee_adjustment' : 'add_fee_adjustment'
        }`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data?.status === 'success') {
        setAdjustmentModalOpen(false);
        setAlert({
          show: true,
          message: editingAdjustment
            ? 'Adjustment updated successfully!'
            : 'Adjustment added successfully!',
          variant: 'success',
        });
        await fetchFeeAdjustments();
      } else {
        setAlert({
          show: true,
          message:
            response.data?.message ||
            `Failed to ${editingAdjustment ? 'update' : 'add'} adjustment.`,
          variant: 'danger',
        });
      }
    } catch (saveError) {
      setAlert({
        show: true,
        message:
          saveError.response?.data?.message ||
          saveError.message ||
          `Error ${editingAdjustment ? 'updating' : 'adding'} adjustment.`,
        variant: 'danger',
      });
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handleDeleteAdjustment = (adjustment) => {
    setAdjustmentToDelete(adjustment);
    setDeleteAdjustmentModalOpen(true);
  };

  const confirmDeleteAdjustment = async () => {
    if (!adjustmentToDelete) {
      return;
    }

    const token = getAuthToken();
    setDeletingAdjustment(true);

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}delete_fee_adjustment`,
        { id: adjustmentToDelete.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data?.status === 'success') {
        setDeleteAdjustmentModalOpen(false);
        setAdjustmentToDelete(null);
        setAlert({
          show: true,
          message: 'Adjustment deleted successfully!',
          variant: 'success',
        });
        await fetchFeeAdjustments();
      } else {
        setAlert({
          show: true,
          message: response.data?.message || 'Failed to delete adjustment.',
          variant: 'danger',
        });
      }
    } catch (deleteError) {
      setAlert({
        show: true,
        message:
          deleteError.response?.data?.message ||
          deleteError.message ||
          'Error deleting adjustment.',
        variant: 'danger',
      });
    } finally {
      setDeletingAdjustment(false);
    }
  };

  const confirmDeleteInstallment = async () => {
    if (!installmentToDelete) {
      return;
    }
    setDeletingInstallment(true);
    const token = getAuthToken();
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}delete_installment`,
        { installment_id: installmentToDelete.installment_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data?.status === 'success') {
        setDeleteConfirmOpen(false);
        setInstallmentToDelete(null);
        setAlert({
          show: true,
          message: 'Installment deleted successfully!',
          variant: 'success',
        });
        fetchStudentData();
      } else {
        setAlert({
          show: true,
          message: response.data?.message || 'Failed to delete installment.',
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message:
          err.response?.data?.message ||
          err.message ||
          'Error deleting installment.',
        variant: 'danger',
      });
    } finally {
      setDeletingInstallment(false);
    }
  };

  const calculateInstallmentProgress = () => {
    const total = installments.length;
    const paid = installments.filter((inst) => inst.status === 'paid').length;
    const due = installments.filter((inst) => inst.status === 'due').length;
    const pending = installments.filter(
      (inst) => inst.status === 'pending',
    ).length;

    return { total, paid, due, pending };
  };

  const calculateAdjustmentSummary = () => {
    return adjustments.reduce(
      (acc, adjustment) => {
        const amount = parseFloat(adjustment.amount) || 0;

        acc.total += amount;
        if (adjustment.type === 'discount') {
          acc.discount += amount;
        } else {
          acc.other += amount;
        }

        return acc;
      },
      {
        total: 0,
        discount: 0,
        other: 0,
      },
    );
  };

  if (loading) {
    return (
      <Layout>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '60vh' }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading fee management...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container mt-4">
          <Alert variant="danger">{error}</Alert>
          <Button variant="outline-primary" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Go Back
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div
        className="container-fluid py-4"
        style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}
      >
        {/* Alert Messages */}
        {alert.show && (
          <div
            style={{
              position: 'fixed',
              left: '18px',
              bottom: '18px',
              zIndex: 9999,
              minWidth: '180px',
              maxWidth: '320px',
              pointerEvents: 'auto',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            }}
          >
            <div
              style={{
                position: 'relative',
                borderRadius: '6px',
                background:
                  alert.variant === 'success'
                    ? '#e6f7e6'
                    : alert.variant === 'danger'
                      ? '#ffeaea'
                      : alert.variant === 'info'
                        ? '#e7f3ff'
                        : '#fff8e1',
                color: '#5c4200',
                marginBottom: 0,
                fontWeight: 500,
                fontSize: '0.92rem',
                padding: '6px 32px 6px 12px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                maxWidth: '320px',
              }}
            >
              <span style={{ flex: 1 }}>{alert.message}</span>
              <button
                onClick={() =>
                  setAlert({ ...alert, show: false, message: '', variant: '' })
                }
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '8px',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#5c4200',
                  fontSize: '1.1rem',
                  lineHeight: 1,
                  padding: '0 2px',
                  cursor: 'pointer',
                  borderRadius: '2px',
                  height: '22px',
                  width: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <Card className="mb-3 shadow-sm">
          <Card.Header className="bg-primary text-white py-2">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">Student Fees</h5>
                <div className="small">
                  <span className="me-3">
                    <strong>Student:</strong> {student?.name || 'Unknown'}
                  </span>
                  {student?.batches?.length > 0 && (
                    <span>
                      <strong>Batch:</strong>{' '}
                      {student.batches
                        .map(
                          (batch) => `${batch.class_name} - ${batch.division}`,
                        )
                        .join(', ')}
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft className="me-1" /> Back
              </Button>
            </div>
          </Card.Header>
        </Card>

        {/* Section 1 - Fee Summary */}
        <Card className="mb-3 shadow-sm">
          <Card.Body className="py-3">
            <Row className="text-center">
              <Col md={4} className="border-end">
                <div className="d-flex align-items-center justify-content-center">
                  <FaDollarSign className="text-primary me-2" size={20} />
                  <div>
                    <div className="fw-bold text-primary fs-5 mb-0">
                      {formatCurrency(summary.totalDue)}
                    </div>
                    <small className="text-muted">Total Fee</small>
                  </div>
                </div>
              </Col>
              <Col md={4} className="border-end">
                <div className="d-flex align-items-center justify-content-center">
                  <FaCheckCircle className="text-success me-2" size={20} />
                  <div>
                    <div className="fw-bold text-success fs-5 mb-0">
                      {formatCurrency(summary.totalPaid)}
                    </div>
                    <small className="text-muted">Total Paid</small>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="d-flex align-items-center justify-content-center">
                  <FaExclamationTriangle
                    className="text-warning me-2"
                    size={20}
                  />
                  <div>
                    <div className="fw-bold text-warning fs-5 mb-0">
                      {formatCurrency(summary.totalPending)}
                    </div>
                    <small className="text-muted">Balance</small>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Section 2 - Fee Assignment (Only if no fee assigned) */}
        {!feeAssigned && (
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Assign Fee Structure</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Academic Year</Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedAcademicYear?.name || ''}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Batch</Form.Label>
                    <Form.Select
                      value={selectedBatchId}
                      onChange={(e) => setSelectedBatchId(e.target.value)}
                      disabled={!student?.batches?.length || assigningFee}
                    >
                      {!student?.batches?.length ? (
                        <option value="">No batch assigned</option>
                      ) : (
                        student.batches.map((batch) => (
                          <option key={batch.batch_id} value={batch.batch_id}>
                            {batch.class_name} - {batch.division}
                          </option>
                        ))
                      )}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Select the batch to use for fee preview and assignment.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Join Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={joinDate}
                      onChange={handleJoinDateChange}
                    />
                    <Form.Text className="text-muted">
                      Join date determines which fee slab is applied.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Fee Preview Section */}
              <div className="mt-4">
                <h6 className="text-primary mb-3">
                  <FaMoneyBillWave className="me-2" />
                  Fee Preview
                </h6>

                {previewLoading ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Loading fee preview...</span>
                  </div>
                ) : previewError ? (
                  <Alert variant="warning" className="py-2">
                    <small>{previewError}</small>
                  </Alert>
                ) : feePreview ? (
                  <Card className="bg-light border-0">
                    <Card.Body className="py-3">
                      <Row>
                        <Col md={6}>
                          <div className="mb-2">
                            <strong>Fee Structure:</strong>{' '}
                            <span className="text-primary">
                              {feePreview.fee_structure}
                            </span>
                          </div>
                          <div className="mb-2">
                            <strong>Applicable Slab:</strong>{' '}
                            <Badge bg="info">{feePreview.slab}</Badge>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-2">
                            <strong>Tuition Fee:</strong>{' '}
                            <span className="text-success">
                              {formatCurrency(feePreview.tuition_fee)}
                            </span>
                          </div>
                          <div className="mb-2">
                            <strong>Discount:</strong>{' '}
                            <span className="text-warning">
                              -{formatCurrency(feePreview.discount)}
                            </span>
                          </div>
                          <div className="mb-0">
                            <strong>Effective Fee:</strong>{' '}
                            <span className="text-primary fw-bold">
                              {formatCurrency(feePreview.effective_fee)}
                            </span>
                          </div>
                        </Col>
                      </Row>

                      {Array.isArray(feePreview.installments) &&
                        feePreview.installments.length > 0 && (
                          <>
                            <hr className="my-3" />
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="mb-0 text-primary">
                                Installment Preview
                              </h6>
                              <Badge bg="secondary">
                                {feePreview.installments.length} Installments
                              </Badge>
                            </div>

                            <Table
                              responsive
                              size="sm"
                              bordered
                              className="mb-2 bg-white"
                            >
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: '80px' }}>#</th>
                                  <th>Title</th>
                                  <th className="text-end">Amount</th>
                                  <th className="text-center">Due After</th>
                                  <th className="text-center">Due Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {feePreview.installments.map((item, index) => (
                                  <tr key={`${item.installment_no}-${index}`}>
                                    <td className="fw-bold text-center">
                                      {item.installment_no}
                                    </td>
                                    <td>{item.title || '-'}</td>
                                    <td className="text-end fw-bold">
                                      {formatCurrency(item.amount || 0)}
                                    </td>
                                    <td className="text-center">
                                      {item.due_after_days ?? 0} days
                                    </td>
                                    <td className="text-center">
                                      {formatPreviewDate(item.due_date)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>

                            <div className="d-flex justify-content-end">
                              <small className="text-muted">
                                Installment Total:{' '}
                                <strong className="text-dark">
                                  {formatCurrency(
                                    feePreview.installments.reduce(
                                      (sum, item) =>
                                        sum + (parseFloat(item.amount) || 0),
                                      0,
                                    ),
                                  )}
                                </strong>
                              </small>
                            </div>
                          </>
                        )}
                    </Card.Body>
                  </Card>
                ) : joinDate ? (
                  <div className="text-center py-3 text-muted">
                    <FaClock className="mb-2" />
                    <div>Select a join date to see fee preview</div>
                  </div>
                ) : null}
              </div>

              <div className="mt-4">
                <Button
                  variant="success"
                  onClick={handleAssignFee}
                  disabled={!selectedBatchId || !feePreview || assigningFee}
                  size="lg"
                >
                  {assigningFee ? (
                    <>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        style={{ width: '14px', height: '14px' }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Assigning...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="me-2" />
                      Assign Fee
                    </>
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Section 3 - Fee Details Tabs */}
        {feeAssigned && (
          <Card className="shadow-sm">
            <Card.Body>
              <style>
                {`
                  .table-success-light {
                    background-color: rgba(25, 135, 84, 0.1) !important;
                  }
                  .table-warning-light {
                    background-color: rgba(255, 193, 7, 0.1) !important;
                  }
                  .table-danger-light {
                    background-color: rgba(220, 53, 69, 0.1) !important;
                  }
                  .installment-cell {
                    display: flex;
                    align-items: center;
                  }
                  .installment-actions-toggle {
                    min-width: 36px;
                    height: 31px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                  }
                  .installment-actions-toggle::after {
                    display: none !important;
                  }
                  .table-menu-wrapper {
                    overflow-x: auto;
                    overflow-y: visible;
                  }
                  .table-menu-wrapper .dropdown-menu {
                    z-index: 1055;
                  }
                `}
              </style>

              <Tabs
                activeKey={activeTab}
                onSelect={(tabKey) => setActiveTab(tabKey || 'installments')}
                className="mb-3"
              >
                <Tab eventKey="installments" title="Installments">
                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                    <div>
                      <h5 className="mb-1">Installments</h5>
                      <small className="text-muted">
                        Manage the student fee schedule for the selected
                        academic year.
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                      {getFeeAssignmentId(feeSheet) && (
                        <Button
                          size="sm"
                          variant={addModalOpen ? 'primary' : 'outline-primary'}
                          disabled={
                            addingInstallment ||
                            undoingAssignment ||
                            assigningFee
                          }
                          onClick={handleOpenAddInstallment}
                        >
                          <FaPlus className="me-1" size={12} />
                          Add Installment
                        </Button>
                      )}
                      {getFeeAssignmentId(feeSheet) && (
                        <Button
                          size="sm"
                          variant={
                            undoingAssignment ? 'danger' : 'outline-danger'
                          }
                          disabled={undoingAssignment || assigningFee}
                          onClick={handleUndoAssignFee}
                          style={{ minWidth: '160px' }}
                        >
                          {undoingAssignment ? (
                            <>
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                style={{ width: '12px', height: '12px' }}
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              Undoing Assignment...
                            </>
                          ) : (
                            <>
                              <FaArrowLeft className="me-1" size={12} />
                              Undo Fee Assignment
                            </>
                          )}
                        </Button>
                      )}
                      {installments.length > 0 && (
                        <div className="installment-progress">
                          <small className="text-muted me-3">
                            <strong>Progress:</strong>
                          </small>
                          <Badge bg="secondary" className="me-2">
                            {calculateInstallmentProgress().total} Total
                          </Badge>
                          <Badge bg="success" className="me-2">
                            {calculateInstallmentProgress().paid} Paid
                          </Badge>
                          <Badge bg="warning" className="me-2">
                            {calculateInstallmentProgress().due} Due
                          </Badge>
                          <Badge bg="light" className="text-dark">
                            {calculateInstallmentProgress().pending} Pending
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  {installments.length === 0 ? (
                    <div className="text-center py-5">
                      <FaClock size={48} className="text-muted mb-3" />
                      <h6 className="text-muted">No installments found</h6>
                    </div>
                  ) : (
                    <div className="table-menu-wrapper">
                      <Table hover className="fee-table mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Installment</th>
                            <th>Amount</th>
                            <th>Paid</th>
                            <th>Balance</th>
                            <th>Due Date</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {installments.map((installment) => {
                            const dueDateStatus = installment.due_date
                              ? getDueDateStatus(
                                  installment.due_date,
                                  installment.balance,
                                  installment.paid,
                                )
                              : null;

                            return (
                              <tr
                                key={installment.installment_id}
                                className={getRowClassName(
                                  installment.status,
                                  installment.due_date,
                                  installment.balance,
                                  installment.paid,
                                )}
                              >
                                <td>
                                  <div className="installment-cell">
                                    {getInstallmentBadge(
                                      installment.installment_no,
                                      installment.status,
                                    )}
                                    <div>
                                      <div className="fw-bold">
                                        Installment {installment.installment_no}
                                      </div>
                                      <small className="text-muted">
                                        {installment.title}
                                      </small>
                                    </div>
                                  </div>
                                </td>
                                <td className="fw-bold">
                                  {formatCurrency(installment.amount)}
                                </td>
                                <td className="text-success fw-bold">
                                  {formatCurrency(installment.paid)}
                                </td>
                                <td className="text-danger fw-bold">
                                  {formatCurrency(installment.balance)}
                                </td>
                                <td className="text-center">
                                  {installment.due_date ? (
                                    <div className="d-flex flex-column align-items-center">
                                      <div
                                        className="text-dark fw-bold"
                                        style={{ fontSize: '0.85rem' }}
                                      >
                                        {new Date(
                                          installment.due_date,
                                        ).toLocaleDateString('en-GB', {
                                          day: '2-digit',
                                          month: 'short',
                                          year: 'numeric',
                                        })}
                                      </div>
                                      {dueDateStatus &&
                                        dueDateStatus.type === 'overdue' && (
                                          <small
                                            className="text-danger"
                                            style={{ fontSize: '0.65rem' }}
                                          >
                                            <i className="fas fa-exclamation-triangle me-1"></i>
                                            {dueDateStatus.daysDiff} days
                                            overdue
                                          </small>
                                        )}
                                      {dueDateStatus &&
                                        dueDateStatus.type === 'due_today' && (
                                          <small
                                            className="text-warning"
                                            style={{ fontSize: '0.65rem' }}
                                          >
                                            <i className="fas fa-clock me-1"></i>
                                            Due today!
                                          </small>
                                        )}
                                      {dueDateStatus &&
                                        dueDateStatus.type === 'due_soon' && (
                                          <small
                                            className="text-warning"
                                            style={{ fontSize: '0.65rem' }}
                                          >
                                            <i className="fas fa-clock me-1"></i>
                                            Due in {dueDateStatus.daysDiff} days
                                          </small>
                                        )}
                                    </div>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  {getStatusBadge(
                                    installment.status,
                                    installment.balance,
                                    installment.due_date,
                                    installment.paid,
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-2 align-items-center flex-wrap">
                                    {installment.status === 'pending' ? (
                                      <Button
                                        size="sm"
                                        variant={
                                          makingDueId ===
                                          installment.installment_id
                                            ? 'warning'
                                            : 'outline-warning'
                                        }
                                        disabled={
                                          makingDueId ===
                                            installment.installment_id ||
                                          feeLoading
                                        }
                                        style={{ minWidth: '120px' }}
                                        onClick={() =>
                                          handleMakeDue(
                                            installment.installment_id,
                                          )
                                        }
                                      >
                                        {makingDueId ===
                                        installment.installment_id ? (
                                          <>
                                            <div
                                              className="spinner-border spinner-border-sm me-2"
                                              role="status"
                                              style={{
                                                width: '12px',
                                                height: '12px',
                                              }}
                                            >
                                              <span className="visually-hidden">
                                                Loading...
                                              </span>
                                            </div>
                                            Making Due...
                                          </>
                                        ) : (
                                          <>
                                            <FaClock
                                              className="me-1"
                                              size={12}
                                            />
                                            Make Due
                                          </>
                                        )}
                                      </Button>
                                    ) : installment.status === 'due' ? (
                                      <Button
                                        size="sm"
                                        variant={
                                          undoingDueId ===
                                          installment.installment_id
                                            ? 'danger'
                                            : 'outline-danger'
                                        }
                                        disabled={
                                          undoingDueId ===
                                            installment.installment_id ||
                                          feeLoading
                                        }
                                        style={{ minWidth: '120px' }}
                                        onClick={() =>
                                          handleUndoMakeDue(
                                            installment.installment_id,
                                          )
                                        }
                                      >
                                        {undoingDueId ===
                                        installment.installment_id ? (
                                          <>
                                            <div
                                              className="spinner-border spinner-border-sm me-2"
                                              role="status"
                                              style={{
                                                width: '12px',
                                                height: '12px',
                                              }}
                                            >
                                              <span className="visually-hidden">
                                                Loading...
                                              </span>
                                            </div>
                                            Undoing...
                                          </>
                                        ) : (
                                          <>
                                            <FaArrowLeft
                                              className="me-1"
                                              size={12}
                                            />
                                            Undo Due
                                          </>
                                        )}
                                      </Button>
                                    ) : installment.status === 'paid' ? (
                                      <Badge bg="success" className="px-3 py-2">
                                        <FaCheckCircle
                                          className="me-1"
                                          size={12}
                                        />
                                        Paid
                                      </Badge>
                                    ) : null}

                                    <Dropdown align="end" drop="up">
                                      <Dropdown.Toggle
                                        as={Button}
                                        className="installment-actions-toggle"
                                        size="sm"
                                        variant="outline-secondary"
                                        style={{ lineHeight: 1 }}
                                        disabled={
                                          makingDueId ===
                                            installment.installment_id ||
                                          undoingDueId ===
                                            installment.installment_id
                                        }
                                      >
                                        <FaEllipsisV size={12} />
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu
                                        renderOnMount
                                        popperConfig={{
                                          strategy: 'fixed',
                                          modifiers: [
                                            {
                                              name: 'offset',
                                              options: { offset: [0, 8] },
                                            },
                                            {
                                              name: 'preventOverflow',
                                              options: {
                                                rootBoundary: 'viewport',
                                                altAxis: true,
                                              },
                                            },
                                            {
                                              name: 'flip',
                                              options: {
                                                fallbackPlacements: [
                                                  'top-end',
                                                  'bottom-end',
                                                ],
                                              },
                                            },
                                          ],
                                        }}
                                      >
                                        <Dropdown.Item
                                          onClick={() =>
                                            handleEditInstallment(installment)
                                          }
                                        >
                                          <FaEdit
                                            className="me-2 text-primary"
                                            size={13}
                                          />
                                          Edit
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          className="text-danger"
                                          onClick={() =>
                                            handleDeleteInstallment(installment)
                                          }
                                        >
                                          <FaTrash className="me-2" size={13} />
                                          Delete
                                        </Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Tab>

                <Tab eventKey="adjustments" title="Adjuestments">
                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                    <div>
                      <h5 className="mb-1">Adjuestments</h5>
                      <small className="text-muted">
                        Maintain discounts and other fee adjustments for this
                        student.
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                      <Button
                        size="sm"
                        variant={
                          adjustmentModalOpen ? 'primary' : 'outline-primary'
                        }
                        disabled={savingAdjustment}
                        onClick={handleOpenAddAdjustment}
                      >
                        <FaPlus className="me-1" size={12} />
                        Add Adjustment
                      </Button>
                      {adjustments.length > 0 && (
                        <>
                          <Badge bg="danger">
                            Discounts{' '}
                            {formatCurrency(
                              calculateAdjustmentSummary().discount,
                            )}
                          </Badge>
                          <Badge bg="secondary">
                            Others{' '}
                            {formatCurrency(calculateAdjustmentSummary().other)}
                          </Badge>
                          <Badge bg="dark">
                            Total{' '}
                            {formatCurrency(calculateAdjustmentSummary().total)}
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>

                  {adjustmentsLoading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-3 mb-0">Loading adjustments...</p>
                    </div>
                  ) : adjustments.length === 0 ? (
                    <div className="text-center py-5">
                      <FaPercentage size={42} className="text-muted mb-3" />
                      <h6 className="text-muted">No adjustments found</h6>
                    </div>
                  ) : (
                    <div className="table-menu-wrapper">
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Title</th>
                            <th>Type</th>
                            <th>Amount</th>
                            <th>Created At</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adjustments.map((adjustment) => (
                            <tr key={adjustment.id}>
                              <td className="fw-semibold">
                                {adjustment.title}
                              </td>
                              <td>
                                <Badge
                                  bg={
                                    adjustment.type === 'discount'
                                      ? 'danger'
                                      : 'secondary'
                                  }
                                >
                                  {adjustment.type}
                                </Badge>
                              </td>
                              <td className="fw-bold">
                                {formatCurrency(adjustment.amount)}
                              </td>
                              <td>
                                {formatAdjustmentDateTime(
                                  adjustment.created_at,
                                )}
                              </td>
                              <td>
                                <Dropdown align="end" drop="up">
                                  <Dropdown.Toggle
                                    as={Button}
                                    className="installment-actions-toggle"
                                    size="sm"
                                    variant="outline-secondary"
                                    style={{ lineHeight: 1 }}
                                    disabled={
                                      savingAdjustment || deletingAdjustment
                                    }
                                  >
                                    <FaEllipsisV size={12} />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu
                                    renderOnMount
                                    popperConfig={{
                                      strategy: 'fixed',
                                      modifiers: [
                                        {
                                          name: 'offset',
                                          options: { offset: [0, 8] },
                                        },
                                        {
                                          name: 'preventOverflow',
                                          options: {
                                            rootBoundary: 'viewport',
                                            altAxis: true,
                                          },
                                        },
                                        {
                                          name: 'flip',
                                          options: {
                                            fallbackPlacements: [
                                              'top-end',
                                              'bottom-end',
                                            ],
                                          },
                                        },
                                      ],
                                    }}
                                  >
                                    <Dropdown.Item
                                      onClick={() =>
                                        handleOpenEditAdjustment(adjustment)
                                      }
                                    >
                                      <FaEdit
                                        className="me-2 text-primary"
                                        size={13}
                                      />
                                      Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      className="text-danger"
                                      onClick={() =>
                                        handleDeleteAdjustment(adjustment)
                                      }
                                    >
                                      <FaTrash className="me-2" size={13} />
                                      Delete
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        )}
      </div>

      {/* Adjustment Delete Confirmation Modal */}
      <Modal
        show={deleteAdjustmentModalOpen}
        onHide={() =>
          !deletingAdjustment && setDeleteAdjustmentModalOpen(false)
        }
        centered
        size="sm"
      >
        <Modal.Header closeButton={!deletingAdjustment}>
          <Modal.Title>
            <FaTrash className="me-2 text-danger" />
            Delete Adjustment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {adjustmentToDelete && (
            <p className="mb-0">
              Are you sure you want to delete{' '}
              <strong>{adjustmentToDelete.title}</strong>?
              <br />
              <small className="text-danger">
                This action cannot be undone.
              </small>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDeleteAdjustmentModalOpen(false)}
            disabled={deletingAdjustment}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={confirmDeleteAdjustment}
            disabled={deletingAdjustment}
          >
            {deletingAdjustment ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              <>
                <FaTrash className="me-2" size={12} />
                Delete
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit Adjustment Modal */}
      <Modal
        show={adjustmentModalOpen}
        onHide={() => !savingAdjustment && setAdjustmentModalOpen(false)}
        centered
      >
        <Modal.Header closeButton={!savingAdjustment}>
          <Modal.Title>
            {editingAdjustment ? (
              <FaEdit className="me-2" />
            ) : (
              <FaPlus className="me-2" />
            )}
            {editingAdjustment ? 'Edit Adjustment' : 'Add Adjustment'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={adjustmentForm.title}
                onChange={(e) =>
                  setAdjustmentForm((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                placeholder="e.g. Scholarship"
                disabled={savingAdjustment}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={adjustmentForm.type}
                onChange={(e) =>
                  setAdjustmentForm((prev) => ({
                    ...prev,
                    type: e.target.value,
                  }))
                }
                disabled={savingAdjustment}
              >
                <option value="discount">Discount</option>
                <option value="addition">Addition</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={adjustmentForm.amount}
                onChange={(e) =>
                  setAdjustmentForm((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                placeholder="Enter amount"
                disabled={savingAdjustment}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setAdjustmentModalOpen(false)}
            disabled={savingAdjustment}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAdjustment}
            disabled={savingAdjustment}
          >
            {savingAdjustment ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              <>
                {editingAdjustment ? (
                  <FaCheckCircle className="me-2" />
                ) : (
                  <FaPlus className="me-2" />
                )}
                {editingAdjustment ? 'Save Changes' : 'Add Adjustment'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteConfirmOpen}
        onHide={() => !deletingInstallment && setDeleteConfirmOpen(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton={!deletingInstallment}>
          <Modal.Title>
            <FaTrash className="me-2 text-danger" />
            Delete Installment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {installmentToDelete && (
            <p className="mb-0">
              Are you sure you want to delete{' '}
              <strong>
                {installmentToDelete.title ||
                  `Installment ${installmentToDelete.installment_no}`}
              </strong>
              ?
              <br />
              <small className="text-danger">
                This action cannot be undone.
              </small>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={deletingInstallment}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={confirmDeleteInstallment}
            disabled={deletingInstallment}
          >
            {deletingInstallment ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              <>
                <FaTrash className="me-2" size={12} />
                Delete
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Installment Modal */}
      <Modal
        show={addModalOpen}
        onHide={() => !addingInstallment && setAddModalOpen(false)}
        centered
      >
        <Modal.Header closeButton={!addingInstallment}>
          <Modal.Title>
            <FaPlus className="me-2" />
            Add Installment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={addForm.title}
                onChange={(e) =>
                  setAddForm((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="e.g. Special Fee"
                disabled={addingInstallment}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={addForm.amount}
                onChange={(e) =>
                  setAddForm((prev) => ({ ...prev, amount: e.target.value }))
                }
                placeholder="Enter amount"
                disabled={addingInstallment}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                type="date"
                value={addForm.due_date}
                onChange={(e) =>
                  setAddForm((prev) => ({ ...prev, due_date: e.target.value }))
                }
                disabled={addingInstallment}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setAddModalOpen(false)}
            disabled={addingInstallment}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAddInstallment}
            disabled={addingInstallment}
          >
            {addingInstallment ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Adding...
              </>
            ) : (
              <>
                <FaPlus className="me-2" />
                Add Installment
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Installment Modal */}
      <Modal
        show={editModalOpen}
        onHide={() => !savingEdit && setEditModalOpen(false)}
        centered
      >
        <Modal.Header closeButton={!savingEdit}>
          <Modal.Title>
            <FaEdit className="me-2" />
            Edit Installment
            {editingInstallment && (
              <small className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>
                #{editingInstallment.installment_no}
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={editForm.title}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="e.g. Tuition Fee - Term 1"
                disabled={savingEdit}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={editForm.amount}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, amount: e.target.value }))
                }
                placeholder="Enter amount"
                disabled={savingEdit}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                type="date"
                value={editForm.due_date}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, due_date: e.target.value }))
                }
                disabled={savingEdit}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEditModalOpen(false)}
            disabled={savingEdit}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEdit}
            disabled={savingEdit}
          >
            {savingEdit ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              <>
                <FaCheckCircle className="me-2" />
                Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default NewFeeManagement;
